import { NgTemplateOutlet } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  model,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormGroup, ValidatorFn } from '@angular/forms';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxOptional, OnyxTime } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxFormGroupComponent,
  onyxMaxDateTimeValidator,
  onyxMinDateTimeValidator,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
  OnyxToastService,
} from '@onyx/angular';
import { isEqual, mapValues } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  merge,
  skip,
  startWith,
  switchMap,
} from 'rxjs';
import { FleetService } from '../../../../../dashboard/fleet/common/services/fleet.service';
import { OrderPointStatus } from '../../../../../dashboard/orders/common/enums/order-point-status';
import type { SimplifiedOrder } from '../../../../../dashboard/orders/common/interfaces/order';
import { OrdersService } from '../../../../../dashboard/orders/common/services/orders.service';
import { RoutePointState } from '../../../../enums/route/route-point-state';
import { CommonHelper } from '../../../../helpers/common.helper';
import { RouteHelper } from '../../../../helpers/route.helper';
import { SuggestionHelper } from '../../../../helpers/suggestions.helper';
import { ValidationHelper } from '../../../../helpers/validation.helper';
import type { RoutePoint } from '../../../../services/route.service';
import { RouteService } from '../../../../services/route.service';

export type RoutePointStatusFormGroup = ReturnType<
  RoutePointStatusFormComponent['buildForm']
>;
export type RoutePointStatusForm = ReturnType<
  RoutePointStatusFormGroup['getRawValue']
>;

type RoutePointStatusFormDateTimeForm = ReturnType<
  ReturnType<RoutePointStatusFormComponent['buildDateTimeForm']>['getRawValue']
>;

export interface RoutePointStatusFormDto {
  status: OrderPointStatus;
  arrivedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

@Component({
  selector: 'app-route-point-status-form',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    OnyxFormGroupComponent,
    OnyxDatepickerComponent,
    OnyxTimepickerComponent,
    OnyxSuggestionsComponent,
    OnyxButtonComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './route-point-status-form.component.html',
  styleUrl: './route-point-status-form.component.scss',
  host: {
    '[class.compact]': `['map', 'swapPanel'].includes(context)`,
    '[class.modal]': `variant() === 'modal'`,
  },
})
export class RoutePointStatusFormComponent implements OnInit {
  private readonly routeService = inject(RouteService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly ordersService = inject(OrdersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly injector = inject(Injector);
  private readonly fleetService = inject(FleetService);

  public readonly point = input.required<RoutePoint>();
  public readonly order = input<OnyxOptional<SimplifiedOrder>>();
  public readonly variant = input<'route' | 'modal'>('route');
  public readonly loading = model(false);
  public readonly disabled = input(false);
  public readonly submit$ = input<Subject<void>>();
  public readonly close$ = input<Subject<void>>();

  protected readonly I18N = 'route.statusForm';
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
    { future: false },
  );
  protected readonly TIME_SUGGESTIONS = SuggestionHelper.getTimeSuggestions();
  protected readonly TODAY = DateTime.now().toISODate();

  protected readonly context = this.routeService.context;

  protected form!: RoutePointStatusFormGroup;

  public ngOnInit(): void {
    this.form = this.buildForm();
    this.updateValidators();

    const controls = this.form.controls;
    merge(
      this.form.valueChanges.pipe(startWith(null)),
      toObservable(this.routeService.points, { injector: this.injector }),
      toObservable(this.routeService.order, { injector: this.injector }),
      toObservable(this.order, { injector: this.injector }),
    )
      .pipe(
        map(() => this.form.getRawValue()),
        map((value) =>
          mapValues(value, ({ date, time }) => date != null || time != null),
        ),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((validation) => {
        const canRollBackStatus = this.canRollBackStatus();

        for (const [key, required] of Object.entries(validation)) {
          const group = controls[key as keyof typeof validation];
          ValidationHelper.toggleRequiredValidator(
            Object.values(group.controls),
            required || !canRollBackStatus,
          );
          group.updateValueAndValidity();
        }
      });
    this.form.valueChanges
      .pipe(
        map(() => this.form.getRawValue()),
        map((value) => {
          const arrival = this.getDateTime(value.arrival);
          const start = this.getDateTime(value.start);

          return { arrival, start: start ?? arrival };
        }),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ arrival, start }) => {
        controls.start.clearValidators();
        if (arrival) {
          controls.start.addValidators(
            onyxMinDateTimeValidator(arrival) as ValidatorFn,
          );
        }
        controls.start.addValidators(onyxMaxDateTimeValidator() as ValidatorFn);
        this.revalidate(controls.start);

        controls.complete.clearValidators();
        if (start) {
          controls.complete.addValidators(
            onyxMinDateTimeValidator(start) as ValidatorFn,
          );
        }
        controls.complete.addValidators(
          onyxMaxDateTimeValidator() as ValidatorFn,
        );
        this.revalidate(controls.complete);
      });

    toObservable(this.point, { injector: this.injector })
      .pipe(
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
        skip(1),
      )
      .subscribe((point) => {
        const timestamps = RouteHelper.getTimestamps(point);
        if (!timestamps) return;

        this.form.patchValue({
          arrival: CommonHelper.extractDateTimeParts(timestamps.arrivedAt),
          start: CommonHelper.extractDateTimeParts(timestamps.startedAt),
          complete: CommonHelper.extractDateTimeParts(timestamps.completedAt),
        });
        this.updateValidators();
      });

    this.submit$()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      ?.subscribe(() => this.save());

    effect(
      () =>
        ValidationHelper.toggleControls(this.form, {
          enabled: !this.disabled(),
          reset: false,
        }),
      { injector: this.injector },
    );
  }

  public async validate(): Promise<boolean> {
    const isValid = await ValidationHelper.checkValidity(
      this.form,
      this.injector,
    );

    if (!isValid) {
      for (const group of Object.values(this.form.controls)) {
        this.revalidate(group);
      }
    }

    return isValid;
  }

  public submit(): Observable<void> {
    return from(this.validate()).pipe(
      switchMap((isValid) => {
        if (!isValid) return EMPTY;

        const getTimestamp = (form: RoutePointStatusFormDateTimeForm) =>
          this.getDateTime(form)?.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ") ?? null;

        const { arrival, start, complete } = this.form.getRawValue();
        const completedAt = getTimestamp(complete);
        let startedAt = getTimestamp(start);
        let arrivedAt = getTimestamp(arrival);

        let status = OrderPointStatus.TO_DO;
        if (completedAt) {
          startedAt ??= completedAt;
          arrivedAt ??= startedAt;
          status = OrderPointStatus.COMPLETED;
        } else if (startedAt) {
          arrivedAt ??= startedAt;
          status = OrderPointStatus.IN_PROGRESS;
        } else if (arrivedAt) {
          status = OrderPointStatus.WAITING;
        }

        const dto: RoutePointStatusFormDto = {
          status,
          arrivedAt,
          startedAt,
          completedAt,
        };

        const order = this.order();
        const vehicleUuid =
          this.routeService.vehicle()?.uuid ??
          this.routeService.telematicsItem()?.uuid;
        if (!order && !vehicleUuid) return EMPTY;

        const pointUuid = RouteHelper.getUuid(this.point());

        return order
          ? this.ordersService.editPointStatus(
              order.uuid,
              pointUuid,
              vehicleUuid ?? null,
              dto,
            )
          : this.fleetService.editTechnicalPointStatus(
              vehicleUuid!,
              pointUuid,
              dto,
            );
      }),
    );
  }

  protected save(): void {
    if (this.loading()) return;

    const point = this.point();

    this.loading.set(true);
    this.submit()
      .subscribe(() => {
        this.toastService.showSuccess({ message: `${this.I18N}.success` });
        this.close$()?.next();
        this.routeService.togglePointState(point, RoutePointState.DETAILS);
      })
      .add(() => this.loading.set(false));
  }

  protected cancel(): void {
    this.routeService.togglePointState(this.point(), RoutePointState.DETAILS);
  }

  private getDateTime(form: RoutePointStatusFormDateTimeForm): DateTime | null {
    const { date, time } = form;
    if (!date || !time) return null;
    return DateTime.fromISO(`${date}T${time}`);
  }

  private buildForm() {
    const timestamps = RouteHelper.getTimestamps(this.point());
    if (!timestamps) throw new Error('Timestamps not found');

    const { arrivedAt, startedAt, completedAt } = timestamps;
    return this.fb.group({
      arrival: this.buildDateTimeForm(arrivedAt),
      start: this.buildDateTimeForm(startedAt),
      complete: this.buildDateTimeForm(completedAt),
    });
  }

  private buildDateTimeForm(value: string | null) {
    const { date, time } = CommonHelper.extractDateTimeParts(value);

    return this.fb.group(
      {
        date: this.fb.control<string | null>(date),
        time: this.fb.control<OnyxTime | null>(time),
      },
      { validators: [onyxMaxDateTimeValidator()] as ValidatorFn[] },
    );
  }

  private canRollBackStatus(): boolean {
    const point = this.point();
    if (
      RouteHelper.isSwapPoint(point) &&
      RouteHelper.getStatus(point) === OrderPointStatus.COMPLETED
    ) {
      return false;
    }

    if (
      RouteHelper.isOrderCompleted(
        point,
        this.routeService.points(),
        this.order() ?? this.routeService.order(),
      )
    ) {
      return false;
    }
    if (this.routeService.isCurrent(point)) return true;

    const nextPoint = this.routeService
      .points()
      .at(this.routeService.getIndex(point) + 1);
    const nextStatus = nextPoint ? RouteHelper.getStatus(nextPoint) : null;

    return !nextStatus || nextStatus === OrderPointStatus.TO_DO;
  }

  private updateValidators() {
    const index = this.routeService.getIndex(this.point());
    const previousPoint = this.routeService.points().at(index - 1);
    if (index === 0 || !previousPoint) return;

    const previousPointCompletedAt =
      RouteHelper.getTimestamps(previousPoint)?.completedAt;
    if (!previousPointCompletedAt) return;

    this.form.controls.arrival.setValidators([
      onyxMinDateTimeValidator(
        DateTime.fromISO(previousPointCompletedAt) as DateTime,
      ),
      onyxMaxDateTimeValidator(),
    ] as ValidatorFn[]);
    this.revalidate(this.form.controls.arrival);
  }

  private revalidate(group: FormGroup): void {
    for (const control of Object.values(group.controls)) {
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    group.updateValueAndValidity();
  }
}
